import { createAsyncThunk } from '@reduxjs/toolkit';
import { AsyncTaskType } from '../../types/async-task/async-task.type';
import axios from 'axios';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { additionalQueryParams } from '../../utils/utilities';
import { setShowModal, setModalContent } from '../utils/utils.slice';
import {
  CreatePowerCycleDto,
  PowerCycleDetailsType,
  PowerCycleListItemType,
  UpdatePowerCycleDto,
} from '../../types/power-cycle/power-cycle.type';
import { UpdatePowerCycleChannelsDto } from '../../types/power-cycle/power-cycle-channel/power-cycle-channel.type';
import { GrandstreamATAType } from '../../types/ata/grandstream-ata/grandstream-ata.type';
import { ACSATAType } from '../../types/ata/acs-ata/acs-ata.type';
import { MicroserviceRouterType } from '../../types/router/router.type';
import { DialerType } from '../../types/dialer/dialer.type';
import { POWER_CYCLE_DEVICE_TYPE } from '../../utils/constants';
import { PowerCycleTypeType } from '../../types/power-cycle/power-cycle-type/power-cycle-type.type';
import { BatteryType } from '../../types/battery/battery.type';
import { rejectWithApiError } from '../../utils/api';
import { RootReducerState } from '../../redux/Reducers';

export const postPowerCycles = createAsyncThunk<
  AsyncTaskType,
  {
    data: {
      equipment_group: { id: number };
      powerCycles: Array<{
        sn: string;
        password: string;
        is_inventory: boolean;
      }>;
    };
  },
  { state: RootReducerState }
>('powerCycles/postPowerCycles', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<AsyncTaskType>(`${process.env.REACT_APP_API_URL}/power-cycles/bulk`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating powerCycles!' });
  }
});

export const postPowerCycleDetails = createAsyncThunk<
  PowerCycleDetailsType,
  { data: CreatePowerCycleDto },
  { state: RootReducerState }
>('powerCycles/postPowerCycleDetails', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<PowerCycleDetailsType>(`${process.env.REACT_APP_API_URL}/power-cycles`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating powerCycle!' });
  }
});

export const getPowerCyclesList = createAsyncThunk<
  PaginationType<PowerCycleListItemType>,
  RouteParamsType & { controller?: AbortController },
  { state: RootReducerState }
>('powerCycles/getPowerCyclesList/no-loader', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      thunkAPI.getState().sharedReducer.selectedLocation,
      thunkAPI.getState().sharedReducer.includeSuborgs,
      thunkAPI.getState().sharedReducer.selectedCountry
    );
    const mergedParams = { ...params, ...additionalParams };
    const queryString = Object.keys(mergedParams)
      .map(key => key + '=' + mergedParams[key])
      .join('&');

    const response = await axios.get<PaginationType<PowerCycleListItemType>>(
      `${process.env.REACT_APP_API_URL}/power-cycles${queryString ? `?${queryString}` : ''}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching powerCycles!' });
  }
});

export const getPowerCycleDetails = createAsyncThunk<
  PowerCycleDetailsType,
  { id: number },
  { state: RootReducerState }
>('powerCycles/getPowerCycleDetails', async ({ id }, thunkAPI) => {
  try {
    const response = await axios.get<PowerCycleDetailsType>(`${process.env.REACT_APP_API_URL}/power-cycles/${id}`);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching powerCycle!' });
  }
});

export const patchPowerCycleDetails = createAsyncThunk<
  PowerCycleDetailsType,
  { id: number; data: UpdatePowerCycleDto },
  { state: RootReducerState }
>('powerCycles/patchPowerCycleDetails', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<PowerCycleDetailsType>(
      `${process.env.REACT_APP_API_URL}/power-cycles/${id}`,
      data
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating powerCycle!' });
  }
});

export const patchPowerCycleChannels = createAsyncThunk<
  { success: boolean },
  { id: number; data: UpdatePowerCycleChannelsDto },
  { state: RootReducerState }
>('powerCycles/patchPowerCycleChannels', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<{ success: boolean }>(
      `${process.env.REACT_APP_API_URL}/power-cycle-channels/${id}`,
      data
    );
    thunkAPI.dispatch(getPowerCycleDetails({ id }));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating powerCycle channels!' });
  }
});

export const deletePowerCycleDetails = createAsyncThunk<
  PowerCycleDetailsType,
  { id: number; fetchList: boolean },
  { state: RootReducerState }
>('powerCycles/deletePowerCycleDetails', async ({ id, fetchList }, thunkAPI) => {
  try {
    const response = await axios.delete<PowerCycleDetailsType>(`${process.env.REACT_APP_API_URL}/power-cycles/${id}`);
    if (fetchList) thunkAPI.dispatch(getPowerCyclesList(thunkAPI.getState().powerCyclesReducer.powerCyclesRouteParams));
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting powerCycle!' });
  }
});

export const getPowerCycleDevicesList = createAsyncThunk<
  {
    atas: Array<{ id: string; name: string }>;
    acs_atas: Array<{ id: string; name: string }>;
    routers: Array<{ id: string; name: string }>;
    dialers: Array<{ id: string; name: string }>;
    batteries: Array<{ id: string; name: string }>;
  },
  { hash: string; id: number },
  { state: RootReducerState }
>('powerCycles/getPowerCycleDevicesList', async ({ hash, id }, thunkAPI) => {
  try {
    const response = await axios.get<{
      atas: Array<GrandstreamATAType>;
      acs_atas: Array<ACSATAType>;
      routers: Array<MicroserviceRouterType>;
      dialers: Array<DialerType>;
      batteries: Array<BatteryType>;
    }>(`${process.env.REACT_APP_API_URL}/equipment-group/devices/${hash}?power_cycle_id=${id}`);

    let dialers = response.data.dialers;
    if (dialers.length > 0) {
      dialers = dialers?.filter(dialer => {
        const name = dialer?.dialer_type?.name || '';
        return name.toUpperCase().includes('IP');
      });
    }

    return {
      atas:
        response.data?.atas?.map(ata => ({
          id: `${POWER_CYCLE_DEVICE_TYPE.ATA}_${ata?.id}`,
          name: ata?.serial_number || '',
        })) || [],
      acs_atas:
        response.data?.acs_atas?.map(acs_ata => ({
          id: `${POWER_CYCLE_DEVICE_TYPE.ACS_ATA}_${acs_ata?.id}`,
          name: acs_ata?.serial_number || '',
        })) || [],
      routers:
        response.data?.routers?.map(router => ({
          id: `${POWER_CYCLE_DEVICE_TYPE.ROUTER}_${router?.id}`,
          name: router?.serial_number || '',
        })) || [],
      dialers:
        dialers.map(dialer => ({
          id: `${POWER_CYCLE_DEVICE_TYPE.DIALER}_${dialer?.id}`,
          name: dialer?.radio_id || '',
        })) || [],
      batteries: response.data.batteries.map(battery => ({
        id: `${battery?.id}`,
        name: battery?.serial || '',
      })),
    };
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching powerCycle devices!' });
  }
});

export const postPowerCycleChannelCommand = createAsyncThunk<
  PowerCycleDetailsType,
  { id: number; powerCycleID: number },
  { state: RootReducerState }
>('powerCycles/postPowerCycleChannelCommand', async ({ id, powerCycleID }, thunkAPI) => {
  try {
    const response = await axios.post<PowerCycleDetailsType>(
      `${process.env.REACT_APP_API_URL}/power-cycle-channels/${id}/send-command`,
      undefined
    );

    thunkAPI.dispatch(getPowerCycleDetails({ id: powerCycleID }));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during sending powerCycle channel command!' });
  }
});

export const postPowerCycleStatus = createAsyncThunk<
  PowerCycleDetailsType,
  { id: number },
  { state: RootReducerState }
>('powerCycles/postPowerCycleStatus', async ({ id }, thunkAPI) => {
  try {
    const response = await axios.post<PowerCycleDetailsType>(
      `${process.env.REACT_APP_API_URL}/power-cycles/${id}/update-status`,
      undefined
    );
    thunkAPI.dispatch(getPowerCycleDetails({ id }));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during refreshing powerCycle status!' });
  }
});

export const getPowerCycleTypesList = createAsyncThunk<
  Array<Pick<PowerCycleTypeType, 'name' | 'id' | 'numberOfChannels'>>,
  void,
  { state: RootReducerState }
>('powerCycles/getPowerCycleTypesList', async (_, thunkAPI) => {
  try {
    const response = await axios.get<Array<Pick<PowerCycleTypeType, 'name' | 'id' | 'numberOfChannels'>>>(
      `${process.env.REACT_APP_API_URL}/power-cycle-type`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching powerCycles types!' });
  }
});

export const postPowerCycleFixService = createAsyncThunk<
  { success: boolean },
  { id: number; fetchList: boolean },
  { state: RootReducerState }
>('powerCycles/postPowerCycleFixService', async ({ id, fetchList }, thunkAPI) => {
  try {
    const response = await axios.post<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}/power-cycles/${id}/fix-service`, {});

    if (fetchList) thunkAPI.dispatch(getPowerCyclesList(thunkAPI.getState().powerCyclesReducer.powerCyclesRouteParams));
    else thunkAPI.dispatch(getPowerCycleDetails({ id }));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fixing power cycle service!' });
  }
});

export const postPowerCycleOwnershipTransfer = createAsyncThunk<
  void,
  {
    id: number;
    data: {
      owner: { id: number };
    };
  },
  { state: RootReducerState; rejectValue: string }
>('dialers/postPowerCycleOwnershipTransfer', async ({ id, data }, thunkAPI) => {
  try {
    if (!data.owner?.id) {
      return rejectWithApiError({ thunkAPI, error: null, fallback: 'Owner organization is required!' });
    }

    await axios.post(`${process.env.REACT_APP_API_URL}/power-cycles/${id}/ownership-transfer`, data);
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during PowerCycle ownership transfer!' });
  }
});
